import { useEffect, useRef } from 'react'

import { PdfKitSource } from '@harvey-frontend/openapi/models/PdfKitSource'
import * as Sentry from '@sentry/react'
import _ from 'lodash'
import { useAbortController } from 'providers/abort-controller-provider'
import { v4 as uuidv4 } from 'uuid'
import { useShallow } from 'zustand/react/shallow'

import { DraftingApplyEditResponse } from 'openapi/models/DraftingApplyEditResponse'
import { DraftingEditRequest } from 'openapi/models/DraftingEditRequest'
import { DraftingSummarizeAndCiteRequest } from 'openapi/models/DraftingSummarizeAndCiteRequest'
import { KnowledgeSourceType } from 'openapi/models/KnowledgeSourceType'
import { UploadedFile } from 'openapi/models/UploadedFile'

import useQueryAnalytics from 'components/common/analytics/use-query-analytics'

import { transformSelection } from '../../../services/document/edit-utilities'
import { fetchEdits, fetchSummarizeAndCite } from '../../../services/endpoints'
import { EVENT_KIND } from '../../../utils/constants'
import { computeReferenceKey } from '../../file-previewer/utils/helpers'
import { useAccessTokenContext } from '../providers/AccessTokenProvider'
import useAppStore from '../stores/store'
import { ensureError } from '../utils/error-utils'
import { getTextAroundCursor } from '../utils/formatting-utils'
import { getSelection } from '../utils/office'
import { useRequestMode } from './useRequestMode'

export const useFetchEdits = (precedents: UploadedFile[]) => {
  const { addAbortController } = useAbortController()
  const { getAccessToken } = useAccessTokenContext()
  const mode = useRequestMode()
  const {
    documentId,
    eventId,
    highlightedText,
    instruction,
    setQueryError,
    setQueryId,
    setQueryCompleted,
    setEditSummary,
    setIsApplyingSuggestion,
    setHighlightedText,
  } = useAppStore(
    useShallow((s) => ({
      documentId: s.documentId,
      eventId: s.eventId,
      highlightedText: s.highlightedText,
      instruction: s.instruction,
      isApplyingSuggestion: s.isApplyingSuggestion,
      setQueryError: s.setQueryError,
      setQueryId: s.setQueryId,
      setQueryCompleted: s.setQueryCompleted,
      setIsApplyingSuggestion: s.setIsApplyingSuggestion,
      setEditSummary: s.setEditSummary,
      setHighlightedText: s.setHighlightedText,
    }))
  )
  const {
    recordQuerySubmitted,
    recordQueryCancel,
    recordQueryCompletion,
    recordQueryError,
  } = useQueryAnalytics(EVENT_KIND)

  const highlightedTextRef = useRef(highlightedText)
  useEffect(() => {
    highlightedTextRef.current = highlightedText
  }, [highlightedText])

  let applyPromises: Promise<void>[] = []
  const editReferences: Record<string, PdfKitSource> = {}
  const applyEditCallback = (response: DraftingApplyEditResponse) => {
    // This lets us await all the edit applications before updating application state.
    const promise = async () => {
      if (
        !highlightedTextRef.current ||
        highlightedTextRef.current?.isNullObject
      ) {
        return
      }
      try {
        await transformSelection(
          highlightedTextRef.current,
          response.editedText
        )
        highlightedTextRef.current?.select()
        await highlightedTextRef.current?.context.sync()
        await getSelection({ callback: setHighlightedText })
      } catch (error) {
        console.error(error)
      }
    }
    for (const reference of response.references ?? []) {
      editReferences[computeReferenceKey(reference)] = reference
    }
    applyPromises.push(promise())
  }
  return async () => {
    const queryId = uuidv4()
    setQueryId(queryId)
    try {
      const accessToken = await getAccessToken()
      if (!highlightedText || !accessToken) {
        return
      }
      setQueryError(null)
      setQueryCompleted(false)
      setIsApplyingSuggestion(true)
      const abortController = new AbortController()
      addAbortController(mode, abortController)
      const documentContext = await getTextAroundCursor()
      const request: DraftingEditRequest = {
        instructions: instruction,
        textToEdit: highlightedText.text,
        wordDocumentId: documentId,
        eventId,
        documentContext,
      }
      if (precedents.length > 0) {
        request.knowledgeSources = [
          {
            type: KnowledgeSourceType.FILES,
            fileIds: precedents.map((precedent) => precedent.id),
          },
        ]
      }

      recordQuerySubmitted({
        type: mode,
        query_id: queryId,
        clause_length: highlightedText.text.length,
        instruction_length: instruction.length,
        precedents_count: precedents.length,
      })
      const result = await fetchEdits(request)
      applyEditCallback(result)

      if (_.isEmpty(result) || _.every(result, _.isNil)) {
        throw new Error('Failed to generate edits for the given query.')
      }

      recordQueryCompletion({
        type: mode,
        query_id: queryId,
      })

      await Promise.all(applyPromises)

      const summaryRequest: DraftingSummarizeAndCiteRequest = {
        id: result.id || '',
        instructions: instruction,
        originalText: highlightedText.text,
        editedText: result.editedText,
        references: Object.values(editReferences),
      }
      const summaryAndCitations = await fetchSummarizeAndCite(summaryRequest)
      setQueryCompleted(true)
      setEditSummary(summaryAndCitations)
    } catch (e) {
      const error = ensureError(e)
      if (error.name === 'AbortError') {
        setQueryError({ reason: 'Request was canceled.' })
        recordQueryCancel({
          type: mode,
          query_id: queryId,
          clause_length: highlightedText?.text.length ?? 0,
          instruction_length: instruction.length,
          error_message: error.message,
          error_name: error.name,
          error_stack: error.stack ?? '',
        })
      } else {
        Sentry.captureException(error)
        setQueryError({ reason: error.message })
        recordQueryError({
          type: mode,
          clause_length: highlightedText?.text.length ?? 0,
          instruction_length: instruction.length,
          error_message: error.message,
          error_name: error.name,
          error_stack: error.stack ?? '',
          query_id: queryId,
        })
      }
    } finally {
      await getSelection({ callback: setHighlightedText })
      setIsApplyingSuggestion(false)
      applyPromises = []
    }
  }
}
